import { useTranslation } from 'react-i18next';
import { formatLarge, formatPercentage, formatPriceWithCurrency } from '@benzinga/utils';
import { BaseQuoteProfile } from '../../utils/quote/types';
import { SectionTitle } from '@benzinga/core-ui';

const nullCheck = (value: number | string | undefined, callback: () => string): string => {
  return value ? callback() : '–';
};

export const CryptoQuoteMetrics = ({ profile }: { profile: BaseQuoteProfile }) => {
  const { t } = useTranslation('quote');

  const quoteMetrics = [
    {
      label: t('Quote.Metrics.price'),
      value: nullCheck(profile?.richQuoteData?.lastTradePrice, () => {
        const price = profile?.richQuoteData?.lastTradePrice ?? 0;
        return formatPriceWithCurrency(price, 'USD');
      }),
    },
    {
      label: t('Quote.Metrics.24hr-price-change'),
      value: nullCheck(
        Number(profile?.cryptoData?.price_change_24h),
        () => `$${formatLarge(Number(profile?.cryptoData?.price_change_24h))}`,
      ),
    },
    {
      label: t('Quote.Metrics.24hr-change'),
      value: nullCheck(
        profile?.cryptoData?.price_change_percentage_24h,
        () => `${formatPercentage(profile?.cryptoData?.price_change_percentage_24h ?? 0)}%`,
      ),
    },
    {
      label: t('Quote.Metrics.market-cap'),
      value: nullCheck(
        Number(profile?.cryptoData?.market_cap),
        () => `$${formatLarge(Number(profile?.cryptoData?.market_cap))}`,
      ),
    },
    {
      label: t('Quote.Metrics.fully-diluted-valuation'),
      value: nullCheck(
        profile?.cryptoData?.fully_diluted_valuation,
        () => `$${formatLarge(profile?.cryptoData?.fully_diluted_valuation ?? 0)}`,
      ),
    },
    {
      label: t('Quote.Metrics.total-volume'),
      value: nullCheck(profile?.cryptoData?.total_volume, () => formatLarge(profile?.cryptoData?.total_volume ?? 0)),
    },
    {
      label: t('Quote.Metrics.all-time-high'),
      value: nullCheck(profile?.cryptoData?.ath, () => `$${profile?.cryptoData?.ath}`),
    },
    {
      label: t('Quote.Metrics.ath-change'),
      value: nullCheck(
        profile?.cryptoData?.ath_change_percentage,
        () => `${formatPercentage(profile?.cryptoData?.ath_change_percentage ?? 0)}%`,
      ),
    },
    {
      label: t('Quote.Metrics.24-hr-high-low'),
      value: nullCheck(
        profile?.cryptoData?.low_24h || profile?.cryptoData?.high_24h,
        () => `$${profile?.cryptoData?.high_24h} / $${profile?.cryptoData?.low_24h}`,
      ),
    },
    {
      label: t('Quote.Metrics.circulating-supply'),
      value: nullCheck(profile?.cryptoData?.circulating_supply, () =>
        formatLarge(profile?.cryptoData?.circulating_supply ?? 0),
      ),
    },
    {
      label: t('Quote.Metrics.total-supply'),
      value: nullCheck(profile?.cryptoData?.total_supply, () => formatLarge(profile?.cryptoData?.total_supply ?? 0)),
    },
    {
      label: t('Quote.Metrics.max-supply'),
      value: nullCheck(profile?.cryptoData?.max_supply, () => formatLarge(profile?.cryptoData?.max_supply ?? 0)),
    },
  ];

  return (
    <div className="card p-4">
      <SectionTitle level={2} className="mb-2">
        {t('Quote.Metrics.key-statistics')}
      </SectionTitle>
      {quoteMetrics.map(metric => (
        <div
          className="flex w-full justify-between py-1.5 text-sm items-center border-b border-bzblue-300 last:border-b-0"
          key={metric.label}
        >
          <div className="label text-gray-500">{metric.label}</div>
          <div className="value font-bold text-right">{metric.value}</div>
        </div>
      ))}
    </div>
  );
};

export default CryptoQuoteMetrics;
